import {
    DashboardTimeRangePreset,
    DashboardTimeRangePresets,
    presetToDateRange,
} from 'pages/Dashboard/DeskActivityOverviewDashboard/components/TimeRangeFilterButton';
import {
    createHashStore,
    HashStoreConfig,
    serializers,
} from 'pages/Dashboard/utils/createHashStore';

export type DeploymentFiltersState = {
    startDateTime: Date;
    endDateTime: Date | undefined;
    locationIds: string[];
    absoluteSliderMinDate: Date;
    absoluteSliderMaxDate: Date;
    timeRangePreset: DashboardTimeRangePreset;
};

export type DeploymentFiltersActions = {
    setStartDateTime: (date: Date) => void;
    setEndDateTime: (date: Date | undefined) => void;
    setLocationIds: (ids: string[]) => void;
    setTimeRangePreset: (preset: DashboardTimeRangePreset | undefined) => void;
    resetFilters: () => void;
};

export type DeploymentFiltersStore = DeploymentFiltersState & DeploymentFiltersActions;

const getDefaultState = (): DeploymentFiltersState => ({
    startDateTime: (() => {
        const { startDateTime } = presetToDateRange('last-year');
        return startDateTime;
    })(),
    endDateTime: undefined,
    locationIds: [],
    absoluteSliderMinDate: new Date(2023, 0, 1),
    absoluteSliderMaxDate: new Date(),
    timeRangePreset: 'last-year',
});

const hashConfig: HashStoreConfig<DeploymentFiltersState> = {
    startDateTime: {
        paramName: 'startDate',
        ...serializers.date,
    },
    endDateTime: {
        paramName: 'endDate',
        ...serializers.optionalDate,
    },
    locationIds: {
        paramName: 'locations',
        ...serializers.stringArray,
    },
    timeRangePreset: {
        paramName: 'timeRange',
        ...serializers.enum<DashboardTimeRangePreset>(DashboardTimeRangePresets),
    },
};

export const useDeploymentFiltersStore = createHashStore<
    DeploymentFiltersState,
    DeploymentFiltersActions
>(
    getDefaultState,
    hashConfig,
    (set, _get) => ({
        // Initialize from hash or defaults (handled by createHashStore)
        ...getDefaultState(),

        // Actions
        setStartDateTime: (date) => set({ startDateTime: date }),
        setEndDateTime: (date) => set({ endDateTime: date }),
        setLocationIds: (ids) => set({ locationIds: ids }),
        setTimeRangePreset: (preset) => set({ timeRangePreset: preset }),
        resetFilters: () => set(getDefaultState()),
    }),
    'deployment-filters'
);
